import { prisma } from '../../shared/prisma';
import { AppError } from '../../shared/errors/AppError';

export async function createCompany(data: { name: string; code?: string; logoUrl?: string; description?: string }) {
  if (!data.name?.trim()) {
    throw new AppError(400, 'Company name is required');
  }
  
  const existing = await prisma.company.findFirst({
    where: { name: { equals: data.name, mode: 'insensitive' } }
  });
  if (existing) {
    throw new AppError(400, `Company "${data.name}" already exists`);
  }

  return (prisma.company as any).create({
    data: {
      name: data.name,
      code: data.code?.trim() ? data.code.trim().toUpperCase() : undefined,
      logoUrl: data.logoUrl || undefined,
      description: data.description
    }
  });
}

export async function listCompanies() {
  return prisma.company.findMany({
    include: { departments: true },
    orderBy: { name: 'asc' }
  });
}

export async function deleteCompany(id: string) {
  return prisma.company.delete({
    where: { id }
  });
}

export async function createDepartment(data: { companyId: string; name: string; description?: string }) {
  if (!data.companyId) {
    throw new AppError(400, 'Company ID is required');
  }
  if (!data.name?.trim()) {
    throw new AppError(400, 'Department name is required');
  }

  const company = await prisma.company.findUnique({
    where: { id: data.companyId }
  });
  if (!company) {
    throw new AppError(404, 'Company not found');
  }

  const existing = await prisma.department.findFirst({
    where: {
      companyId: data.companyId,
      name: { equals: data.name, mode: 'insensitive' }
    }
  });
  if (existing) {
    throw new AppError(400, `Department "${data.name}" already exists in this company`);
  }

  return prisma.department.create({
    data: {
      companyId: data.companyId,
      name: data.name,
      description: data.description
    },
    include: { company: true }
  });
}

export async function listDepartments(companyId?: string) {
  const where = companyId ? { companyId } : {};
  return prisma.department.findMany({
    where,
    include: { company: true },
    orderBy: { name: 'asc' }
  });
}

export async function deleteDepartment(id: string) {
  return prisma.department.delete({
    where: { id }
  });
}

export async function updateCompany(id: string, data: { name?: string; code?: string; logoUrl?: string; description?: string }) {
  if (data.name && !data.name.trim()) {
    throw new AppError(400, 'Company name cannot be empty');
  }

  if (data.name) {
    const existing = await prisma.company.findFirst({
      where: { name: { equals: data.name, mode: 'insensitive' }, id: { not: id } }
    });
    if (existing) {
      throw new AppError(400, `Company "${data.name}" already exists`);
    }
  }

  return (prisma.company as any).update({
    where: { id },
    data: {
      ...(data.name && { name: data.name }),
      ...(data.code !== undefined && { code: data.code?.trim() ? data.code.trim().toUpperCase() : null }),
      ...(data.logoUrl !== undefined && { logoUrl: data.logoUrl || null }),
      ...(data.description !== undefined && { description: data.description })
    }
  });
}

export async function updateDepartment(id: string, data: { name?: string; description?: string }) {
  if (data.name && !data.name.trim()) {
    throw new AppError(400, 'Department name cannot be empty');
  }

  if (data.name) {
    const dept = await prisma.department.findUnique({ where: { id } });
    if (!dept) throw new AppError(404, 'Department not found');

    const existing = await prisma.department.findFirst({
      where: {
        companyId: dept.companyId,
        name: { equals: data.name, mode: 'insensitive' },
        id: { not: id }
      }
    });
    if (existing) {
      throw new AppError(400, `Department "${data.name}" already exists in this company`);
    }
  }

  return prisma.department.update({
    where: { id },
    data: {
      ...(data.name && { name: data.name }),
      ...(data.description !== undefined && { description: data.description })
    },
    include: { company: true }
  });
}

// ─────────────────────────────────────────────
// User Policy Operations
// ─────────────────────────────────────────────

export async function createPolicy(data: { name: string; description?: string; companyIds?: string[]; departmentIds?: string[]; userIds?: string[] }) {
  if (!data.name?.trim()) {
    throw new AppError(400, 'Policy name is required');
  }

  const existing = await prisma.userPolicy.findFirst({
    where: { name: { equals: data.name, mode: 'insensitive' } }
  });
  if (existing) {
    throw new AppError(400, `User Policy "${data.name}" already exists`);
  }

  const policy = await prisma.userPolicy.create({
    data: {
      name: data.name.trim(),
      description: data.description,
      companyIds: data.companyIds || [],
      departmentIds: data.departmentIds || [],
      userIds: data.userIds || [],
    },
    include: {
      _count: { select: { users: true } }
    }
  });

  if (Array.isArray(data.userIds) && data.userIds.length > 0) {
    await prisma.user.updateMany({
      where: { id: { in: data.userIds } },
      data: { policyId: policy.id }
    });
  }

  return policy;
}

export async function listPolicies() {
  return prisma.userPolicy.findMany({
    include: {
      _count: { select: { users: true } }
    },
    orderBy: { createdAt: 'desc' }
  });
}

export async function getPolicy(id: string) {
  const policy = await prisma.userPolicy.findUnique({
    where: { id },
    include: {
      users: {
        select: { id: true, displayName: true, email: true, username: true }
      },
      _count: { select: { users: true } }
    }
  });
  if (!policy) throw new AppError(404, 'User Policy not found');
  return policy;
}

export async function updatePolicy(id: string, data: { name?: string; description?: string; companyIds?: string[]; departmentIds?: string[]; userIds?: string[] }) {
  if (data.name && !data.name.trim()) {
    throw new AppError(400, 'Policy name cannot be empty');
  }

  if (data.name) {
    const existing = await prisma.userPolicy.findFirst({
      where: { name: { equals: data.name, mode: 'insensitive' }, id: { not: id } }
    });
    if (existing) {
      throw new AppError(400, `User Policy "${data.name}" already exists`);
    }
  }

  const updatedPolicy = await prisma.userPolicy.update({
    where: { id },
    data: {
      ...(data.name && { name: data.name.trim() }),
      ...(data.description !== undefined && { description: data.description }),
      ...(data.companyIds !== undefined && { companyIds: data.companyIds }),
      ...(data.departmentIds !== undefined && { departmentIds: data.departmentIds }),
      ...(data.userIds !== undefined && { userIds: data.userIds }),
    },
    include: {
      _count: { select: { users: true } }
    }
  });

  if (Array.isArray(data.userIds)) {
    // Unassign users who were removed from this policy
    await prisma.user.updateMany({
      where: { policyId: id, id: { notIn: data.userIds } },
      data: { policyId: null }
    });
    // Assign users who are included in this policy
    if (data.userIds.length > 0) {
      await prisma.user.updateMany({
        where: { id: { in: data.userIds } },
        data: { policyId: id }
      });
    }
  }

  return updatedPolicy;
}

export async function deletePolicy(id: string) {
  return prisma.userPolicy.delete({
    where: { id }
  });
}

